<!-- Traffic country visibility V1; 2026-10-16 01:18:34 CST; parent 11eaef69bf998ee08cc2542e3a762f0f2f035e1f. -->
<!-- Top IP RTT V1; 2026-10-12 22:01:08 CST; parent b4911ce125fd2d2d4ec0a43767621bf719845f5b. -->
<!-- Console 0.1.142; 2026-10-09 17:34 CST; parent 4658cab6ef4a1d9093517482bc3bfc77645da098; independent cycle panel. -->
<!-- Console 0.1.140; 2026-10-08 17:24 CST; parent 83db177f6d7c30135ab80204b792bebb67d9d79d. -->
<!-- Console 0.1.136; 2026-10-07 00:08:59 CST; parent 4cde4aa014f462d26a36b91dc4334e293a427d48; origin metric provenance fix. -->
<!-- HotX Console 0.1.128；实际修改时间：2026-10-01 12:08:00 CST；精确父基线：8134c31a7bebab26fc7880ade220dc0bb8bfe694；变更：TRAFFIC-TIME-LOCALE-ZH-20261001。 -->
<script setup lang="ts">
import { formatRtt } from '../components/traffic/rtt';
import BandwidthTrendChart from '../components/traffic/BandwidthTrendChart.vue';
import TrafficQuotaCard from '../components/traffic/TrafficQuotaCard.vue';
import TrafficCyclePanel from '../components/traffic/TrafficCyclePanel.vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { consoleApi, hasConsoleSession } from '../api/client';
import { pointApi } from '../api/pointApi';
import type {
  AccessSummary,
  TrafficEvidence,
  CountryStat,
  Domain,
  NodeAnalyticsResponse,
  StatusCodeStat,
  TopIpNodeBreakdown,
  TopIpNodeStat,
  TopIpStat,
  TrafficPoint
} from '../api/types';

/*
 * Data provenance:
 * OpenResty access log -> Node Agent hourly aggregation -> backend log center -> this read-only view.
 * Bandwidth is either the backend bandwidthBps value or bodyBytesSent * 8 / bucket duration.
 * Request counts are never substituted for bandwidth, and absent records are never rendered as healthy traffic.
 */
const ALL_DOMAINS = 'all' as const;
const REFRESH_INTERVAL_OPTIONS = [30, 60, 120] as const;
const COUNTRY_LIMIT = 10;
const TOP_IP_LIMIT = 100;
const TOP_IP_PREVIEW = 10;
const topIpsExpanded = ref(false);
const TOP_IP_NODE_PREVIEW_LIMIT = 3;
const naturalNodeOrder = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' });

type TimeRange = 'today' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';
type Granularity = '5m' | '1h' | '1d';
type DomainSelection = number | typeof ALL_DOMAINS;

interface AccessQuery extends Record<string, unknown> {
  from: string;
  to: string;
  granularity: Granularity;
  domainId?: number;
}

interface BandwidthSample {
  point: TrafficPoint;
  value: number;
}

interface Recommendation {
  bandwidth: number;
  status: '充足' | '建议关注' | '建议升级';
  lowSample: boolean;
  reason: string;
}

interface TopIpDisplay {
  stat: TopIpStat;
  breakdown: TopIpNodeBreakdown | null;
  nodes: TopIpNodeStat[];
}

const domains = ref<Domain[]>([]);
const selectedDomainId = ref<DomainSelection>(ALL_DOMAINS);
const summary = ref<AccessSummary | null>(null);
const topIps = ref<TopIpStat[]>([]);
const statusCodes = ref<StatusCodeStat[]>([]);
const countries = ref<CountryStat[]>([]);
const trafficEvidence = ref<TrafficEvidence | null>(null);
const nodeAnalytics = ref<NodeAnalyticsResponse | null>(null);
const loading = ref(false);
const requesting = ref(false);
const nodeRetrying = ref(false);
const error = ref('');
const nodeError = ref('');
const refreshedAt = ref('');
const timeRange = ref<TimeRange>('1h');
const granularity = ref<Granularity>('5m');
const refreshIntervalSeconds = ref<number>(30);
const customRange = ref<[Date, Date] | null>(null);
const realtime = ref(true);
const countriesExpanded = ref(false);
const expandedTopIps = ref<Set<string>>(new Set());
const showFreeQuota = ref(false);
const lastSuccessfulCoreQuery = ref<AccessQuery | null>(null);
let refreshTimer: number | undefined;
let filterRevision = 0;
let pendingFilterReload = false;

const totalRequests = computed(() => numeric(summary.value?.totalRequests ?? summary.value?.requestCount));
const totalBytes = computed(() => numeric(summary.value?.totalBytes ?? summary.value?.bodyBytesSent));
const cacheHitRate = computed(() => numericOrNull(summary.value?.cacheHitRate ?? summary.value?.cacheHitRatePercent));
const originFailureRate = computed(() =>
  numericOrNull(summary.value?.originFailureRate ?? summary.value?.originFailureRatePercent)
);
function seriesCadenceSeconds(points: TrafficPoint[]) {
  const declared = points.map(point => Number(point.bucketSeconds)).filter(value => Number.isFinite(value) && value > 0);
  if (declared.length) return Math.min(...declared);
  const timestamps = points.map(point => Date.parse(point.bucketStart ?? '')).filter(Number.isFinite).sort((a, b) => a - b);
  const gaps = timestamps.slice(1).map((value, index) => (value - timestamps[index]) / 1000).filter(value => value > 0);
  return gaps.length ? Math.min(...gaps) : Number.POSITIVE_INFINITY;
}

// Pick the finest factual aggregate. This lets the isolated 5m pipeline supersede the legacy hourly response.
const trendPoints = computed<TrafficPoint[]>(() => {
  const access = summary.value?.hourly ?? [];
  const nodeTotal = nodeAnalytics.value?.totalSeries ?? [];
  if (!access.length) return nodeTotal;
  if (!nodeTotal.length) return access;
  return seriesCadenceSeconds(nodeTotal) < seriesCadenceSeconds(access) ? nodeTotal : access;
});
const hasTrafficRecords = computed(() =>
  trendPoints.value.length > 0 || totalRequests.value > 0 || totalBytes.value > 0
);
// Counts must follow the same access-summary response as the displayed failure rate.
// Finer bandwidth series can legitimately omit these fields; absence is never zero.
const originCounts = computed(() => {
  const points = summary.value?.hourly ?? [];
  const valid = (value: unknown): value is number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  if (!points.length || points.some(point => !valid(point.originRequestCount) || !valid(point.originFailureCount))) return null;
  return points.reduce((total, point) => ({
    requests: total.requests + point.originRequestCount!, failures: total.failures + point.originFailureCount!
  }), { requests: 0, failures: 0 });
});
const cacheMetricDetail = computed(() => {
  if (cacheHitRate.value === null) return '节点尚未上报缓存判定数据';
  if (cacheHitRate.value === 0) return '已采集：当前范围未产生缓存命中（仅缓存判定样本）';
  return `命中类 /（命中类 + MISS）；不含 BYPASS、EXPIRED${selectedDomainId.value === ALL_DOMAINS ? '；全部域名汇总，可按域名查看' : ''}`;
});
const cacheAllRequestDetail = computed(() => {
  const counts = trafficEvidence.value?.cache;
  if (!counts) return '全部请求命中占比：暂无完整计数';
  const values = [counts.requests, counts.hits, counts.misses, counts.bypasses];
  if (values.some(value => !Number.isSafeInteger(value) || value < 0)
    || counts.hits + counts.misses + counts.bypasses > counts.requests)
    return '缓存计数不一致，等待核对';
  if (counts.requests === 0) return '当前范围没有请求样本';
  return `全部请求命中占比 ${formatPercent(counts.hits * 100 / counts.requests)}（${formatNumber(counts.hits)} / ${formatNumber(counts.requests)}）；BYPASS ${formatNumber(counts.bypasses)} 次`;
});
const collectorDetail = computed(() => {
  const nodes = trafficEvidence.value?.collectors ?? [];
  if (!nodes.length) return '采集状态未知：尚未收到独立采集报告';
  const labels = { READY: '采集正常', PENDING: '待上报', STALE: '状态过期', ERROR: '采集异常', UNKNOWN: '状态未知' };
  return nodes.map(node => `${node.nodeName}：${labels[node.state] ?? '状态未知'}`).join('；');
});
const originMetricDetail = computed(() => {
  if (originFailureRate.value === null) return '节点尚未上报回源结果数据';
  const counts = originCounts.value;
  if (!counts) return '回源次数暂未完整上报，不能据此认定没有回源';
  if (counts.failures > counts.requests || (counts.requests === 0 && originFailureRate.value > 0)) return '回源统计不一致，请刷新后核对';
  if (counts.requests === 0) return '已采集：当前范围没有回源请求';
  return `已采集：回源 ${formatNumber(counts.requests)} 次，失败 ${formatNumber(counts.failures)} 次`;
});
const bandwidthSamples = computed<BandwidthSample[]>(() =>
  trendPoints.value.flatMap(point => {
    const value = pointBandwidth(point);
    return value === null ? [] : [{ point, value }];
  })
);
const peakSample = computed(() =>
  bandwidthSamples.value.reduce<BandwidthSample | undefined>(
    (peak, sample) => (!peak || sample.value > peak.value ? sample : peak),
    undefined
  )
);
const peakBandwidth = computed(() => peakSample.value?.value ?? fallbackAverageBandwidth.value);
const peakRequestPoint = computed(() =>
  trendPoints.value.reduce<TrafficPoint | undefined>(
    (peak, point) => !peak || numeric(point.requestCount) > numeric(peak.requestCount) ? point : peak,
    undefined
  )
);
const fallbackAverageBandwidth = computed(() => {
  if (!summary.value || totalBytes.value <= 0) return 0;
  return (totalBytes.value * 8) / selectedWindowSeconds();
});
const bandwidthRecommendation = computed<Recommendation>(() => recommendBandwidth(bandwidthSamples.value));
const sortedCountries = computed(() =>
  [...countries.value].sort((left, right) => numeric(right.requestCount) - numeric(left.requestCount))
);
const visibleCountries = computed(() =>
  countriesExpanded.value ? sortedCountries.value : sortedCountries.value.slice(0, COUNTRY_LIMIT)
);
const hiddenCountryCount = computed(() => Math.max(0, sortedCountries.value.length - COUNTRY_LIMIT));
const topIpLeader = computed(() => topIps.value[0]);
const topIpDisplays = computed<TopIpDisplay[]>(() => mergeTopIpNodes(topIps.value, nodeAnalytics.value?.topIpNodes ?? []));
const visibleTopIpDisplays = computed(() => topIpsExpanded.value ? topIpDisplays.value : topIpDisplays.value.slice(0, TOP_IP_PREVIEW));
const otherIpCountries = computed(() => {
  const shown = new Set(visibleTopIpDisplays.value.map(item => item.stat.countryCode?.toUpperCase()));
  return sortedCountries.value.filter(item => !shown.has(item.countryCode?.toUpperCase()));
});
const ddosRiskText = computed(() => {
  const peakRequests = numeric(peakRequestPoint.value?.requestCount);
  const failureRate = originFailureRate.value ?? 0;
  if (peakRequests >= 10000 || failureRate >= 10) return '需要关注';
  if (peakRequests >= 3000 || failureRate >= 3) return '持续观察';
  return '正常观察';
});
const ccRiskText = computed(() => {
  const share = numeric(topIpLeader.value?.requestSharePercent);
  if (share >= 60) return '需要关注';
  if (share >= 35) return '持续观察';
  return '正常观察';
});
const collectionState = computed<'ready' | 'empty' | 'processing' | 'error'>(() => {
  if (error.value && !summary.value) return 'error';
  if (!summary.value) return 'processing';
  return hasTrafficRecords.value ? 'ready' : 'empty';
});

onMounted(async () => {
  if (!hasConsoleSession()) return;
  loading.value = true;
  try {
    // traffic-monitoring-v2: mirror DomainsView's delivery-domain enrichment.
    // The portal payload includes certificate assets and omits originCount, so a
    // real origin lookup is required before applying the shared delivery rule.
    const [domainPage, planOverview] = await Promise.all([
      consoleApi.portal.domains(),
      pointApi.domainPlans()
    ]);
    showFreeQuota.value = !planOverview.activePush;
    const domainsWithOrigins = await Promise.all((domainPage.domains || []).map(async domain => {
      if (domain.accessMode === 'REDIRECT_301') return domain;
      try {
        const origins = await consoleApi.origins.list(domain.id);
        return { ...domain, originCount: origins.length };
      } catch {
        return { ...domain, originCount: Number(domain.originCount || 0) };
      }
    }));
    domains.value = domainsWithOrigins.filter(isDeliveryDomain);
    if (domains.value.length) await loadTraffic();
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    loading.value = false;
  }

  restartAutoRefresh();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

function restartAutoRefresh() {
  if (refreshTimer !== undefined) window.clearInterval(refreshTimer);
  refreshTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && realtime.value) void loadTraffic(true);
  }, refreshIntervalSeconds.value * 1_000);
}

onBeforeUnmount(() => {
  if (refreshTimer !== undefined) window.clearInterval(refreshTimer);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

function isDeliveryDomain(domain: Domain) {
  return domain.accessMode === 'REDIRECT_301' || Number(domain.originCount || 0) > 0;
}

async function loadTraffic(silent = false) {
  if (!domains.value.length) return;
  if (requesting.value) {
    if (!silent) pendingFilterReload = true;
    return;
  }
  const requestRevision = filterRevision;
  requesting.value = true;
  if (!silent || !summary.value) loading.value = true;
  error.value = '';

  try {
    const query = buildAccessQuery();
    const [coreResult, nodeResult, evidenceResult] = await Promise.allSettled([
      Promise.all([
        consoleApi.logs.access(query),
        consoleApi.logs.topIps({ ...query, limit: TOP_IP_LIMIT }),
        consoleApi.logs.statusCodes(query),
        consoleApi.logs.countries({ ...query, limit: 100 })
      ]),
      consoleApi.logs.nodeAnalytics({ ...query, limit: TOP_IP_LIMIT }),
      consoleApi.logs.trafficEvidence(query)
    ]);

    if (requestRevision !== filterRevision) return;
    if (coreResult.status === 'rejected') throw coreResult.reason;
    const [accessSummary, ipStats, statusStats, countryStats] = coreResult.value;
    summary.value = accessSummary;
    trafficEvidence.value = evidenceResult.status === 'fulfilled' ? evidenceResult.value : null;
    topIps.value = ipStats;
    statusCodes.value = statusStats;
    countries.value = countryStats;
    lastSuccessfulCoreQuery.value = { ...query };
    if (nodeResult.status === 'fulfilled') {
      nodeAnalytics.value = nodeResult.value;
      nodeError.value = '';
    } else {
      nodeAnalytics.value = null;
      nodeError.value = errorMessage(nodeResult.reason);
    }
    refreshedAt.value = new Date().toLocaleString('zh-CN');
  } catch (cause) {
    if (requestRevision === filterRevision) error.value = errorMessage(cause);
  } finally {
    requesting.value = false;
    loading.value = false;
    if (pendingFilterReload) {
      pendingFilterReload = false;
      void loadTraffic();
    }
  }
}

async function retryNodeAnalytics() {
  if (!lastSuccessfulCoreQuery.value || nodeRetrying.value) return;
  const retryQuery = { ...lastSuccessfulCoreQuery.value };
  const retryRevision = filterRevision;
  nodeRetrying.value = true;
  try {
    const response = await consoleApi.logs.nodeAnalytics({
      ...retryQuery,
      limit: TOP_IP_LIMIT
    });
    if (retryRevision !== filterRevision || !sameAccessQuery(lastSuccessfulCoreQuery.value, retryQuery)) return;
    nodeAnalytics.value = response;
    nodeError.value = '';
  } catch (cause) {
    if (retryRevision !== filterRevision || !sameAccessQuery(lastSuccessfulCoreQuery.value, retryQuery)) return;
    nodeAnalytics.value = null;
    nodeError.value = errorMessage(cause);
  } finally {
    nodeRetrying.value = false;
  }
}

function sameAccessQuery(left: AccessQuery | null, right: AccessQuery) {
  return left?.from === right.from
    && left.to === right.to
    && left.domainId === right.domainId
    && left.granularity === right.granularity;
}

function buildAccessQuery(): AccessQuery {
  const now = new Date();
  let from: Date;
  let to = now;
  if (timeRange.value === 'custom' && customRange.value) {
    [from, to] = customRange.value.map(value => new Date(value)) as [Date, Date];
  } else if (timeRange.value === 'today') {
    from = new Date(now);
    from.setHours(0, 0, 0, 0);
  } else {
    const duration = timeRangeDuration(timeRange.value);
    from = new Date(now.getTime() - duration);
  }

  const query: AccessQuery = {
    from: from.toISOString(),
    to: to.toISOString(),
    granularity: granularity.value
  };
  if (selectedDomainId.value !== ALL_DOMAINS) query.domainId = selectedDomainId.value;
  return query;
}

async function selectTimeRange(value: TimeRange) {
  timeRange.value = value;
  customRange.value = null;
  realtime.value = true;
  granularity.value = ['today', '1h', '6h', '24h'].includes(value) ? '5m' : value === '7d' ? '1h' : '1d';
  filterRevision += 1;
  await loadTraffic();
}

async function applyCustomRange(value: [Date, Date] | null) {
  if (!value || value.length !== 2) return;
  const [from, to] = value.map(item => new Date(item)) as [Date, Date];
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from >= to) return;
  customRange.value = [from, to];
  timeRange.value = 'custom';
  realtime.value = false;
  const duration = to.getTime() - from.getTime();
  granularity.value = duration <= 86_400_000 ? '5m' : duration <= 604_800_000 ? '1h' : '1d';
  filterRevision += 1;
  await loadTraffic();
}

async function panTimeWindow(direction: -1 | 1) {
  const query = lastSuccessfulCoreQuery.value ?? buildAccessQuery();
  const from = new Date(query.from);
  const to = new Date(query.to);
  const shift = Math.max(300_000, (to.getTime() - from.getTime()) / 2) * direction;
  const nextTo = new Date(Math.min(Date.now(), to.getTime() + shift));
  const nextFrom = new Date(nextTo.getTime() - (to.getTime() - from.getTime()));
  customRange.value = [nextFrom, nextTo];
  timeRange.value = 'custom';
  realtime.value = nextTo.getTime() >= Date.now() - 60_000;
  filterRevision += 1;
  await loadTraffic();
}

async function returnToRealtime() {
  customRange.value = null;
  timeRange.value = '1h';
  granularity.value = '5m';
  realtime.value = true;
  filterRevision += 1;
  await loadTraffic();
}

function timeRangeDuration(value: TimeRange) {
  const durations: Partial<Record<TimeRange, number>> = {
    '1h': 3_600_000,
    '6h': 21_600_000,
    '24h': 86_400_000,
    '7d': 604_800_000,
    '30d': 2_592_000_000
  };
  return durations[value] ?? 3_600_000;
}

async function selectDomain() {
  countriesExpanded.value = false;
  topIpsExpanded.value = false;
  filterRevision += 1;
  await loadTraffic();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible' && realtime.value) void loadTraffic(true);
}

function mergeTopIpNodes(stats: TopIpStat[], breakdowns: TopIpNodeBreakdown[]): TopIpDisplay[] {
  const matches: Array<TopIpNodeBreakdown | null> = stats.map(() => null);
  const usedBreakdowns = new Set<number>();
  const exactBreakdowns = new Map<string, number[]>();

  breakdowns.forEach((breakdown, index) => {
    const indexes = exactBreakdowns.get(breakdown.clientIp) ?? [];
    indexes.push(index);
    exactBreakdowns.set(breakdown.clientIp, indexes);
  });

  stats.forEach((stat, statIndex) => {
    const exactIndex = exactBreakdowns.get(stat.clientIp)?.find(index => !usedBreakdowns.has(index));
    if (exactIndex === undefined) return;
    matches[statIndex] = breakdowns[exactIndex];
    usedBreakdowns.add(exactIndex);
  });

  const unmatchedStats = new Map<string, number[]>();
  stats.forEach((stat, statIndex) => {
    if (matches[statIndex]) return;
    const normalized = normalizeIpLiteral(stat.clientIp);
    if (!normalized) return;
    const indexes = unmatchedStats.get(normalized) ?? [];
    indexes.push(statIndex);
    unmatchedStats.set(normalized, indexes);
  });
  const unmatchedBreakdowns = new Map<string, number[]>();
  breakdowns.forEach((breakdown, breakdownIndex) => {
    if (usedBreakdowns.has(breakdownIndex)) return;
    const normalized = normalizeIpLiteral(breakdown.clientIp);
    if (!normalized) return;
    const indexes = unmatchedBreakdowns.get(normalized) ?? [];
    indexes.push(breakdownIndex);
    unmatchedBreakdowns.set(normalized, indexes);
  });

  unmatchedStats.forEach((statIndexes, normalized) => {
    const breakdownIndexes = unmatchedBreakdowns.get(normalized) ?? [];
    if (statIndexes.length !== 1 || breakdownIndexes.length !== 1) return;
    matches[statIndexes[0]] = breakdowns[breakdownIndexes[0]];
  });

  return stats.map((stat, index) => ({
    stat,
    breakdown: matches[index],
    nodes: [...(matches[index]?.nodes ?? [])].sort(compareTopIpNodes)
  }));
}

function compareTopIpNodes(left: TopIpNodeStat, right: TopIpNodeStat) {
  const byRequests = numeric(right.requestCount) - numeric(left.requestCount);
  if (byRequests) return byRequests;
  const byCode = naturalNodeOrder.compare(nodeCodeLabel(left), nodeCodeLabel(right));
  return byCode || left.nodeId - right.nodeId;
}

function normalizeIpLiteral(rawValue: string) {
  const value = rawValue.trim();
  const ipv4 = parseIpv4(value);
  if (ipv4) return `ipv4:${ipv4.join('.')}`;
  const ipv6 = parseIpv6(value);
  return ipv6 ? `ipv6:${ipv6.map(part => part.toString(16).padStart(4, '0')).join(':')}` : null;
}

function parseIpv4(value: string) {
  if (!/^(?:\d{1,3}\.){3}\d{1,3}$/.test(value)) return null;
  const parts = value.split('.').map(part => Number(part));
  return parts.every(part => Number.isInteger(part) && part >= 0 && part <= 255) ? parts : null;
}

function parseIpv6(value: string) {
  if (!value.includes(':') || value.includes('%') || /[^0-9a-fA-F:.]/.test(value)) return null;
  let candidate = value.toLowerCase();
  if (candidate.includes('.')) {
    const lastColon = candidate.lastIndexOf(':');
    if (lastColon < 0) return null;
    const ipv4 = parseIpv4(candidate.slice(lastColon + 1));
    if (!ipv4) return null;
    candidate = `${candidate.slice(0, lastColon + 1)}${((ipv4[0] << 8) | ipv4[1]).toString(16)}:${((ipv4[2] << 8) | ipv4[3]).toString(16)}`;
  }

  const compressedParts = candidate.split('::');
  if (compressedParts.length > 2) return null;
  const compressed = compressedParts.length === 2;
  const head = parseIpv6Side(compressedParts[0]);
  const tail = compressed ? parseIpv6Side(compressedParts[1]) : [];
  if (!head || !tail) return null;
  if (!compressed) return head.length === 8 ? head : null;
  const missing = 8 - head.length - tail.length;
  return missing >= 1 ? [...head, ...Array.from({ length: missing }, () => 0), ...tail] : null;
}

function parseIpv6Side(value: string) {
  if (!value) return [];
  const parts = value.split(':');
  if (parts.some(part => !/^[0-9a-f]{1,4}$/.test(part))) return null;
  return parts.map(part => Number.parseInt(part, 16));
}

function topIpKey(item: TopIpDisplay, index: number) {
  return `${index}:${item.stat.clientIp}`;
}

function visibleTopIpNodes(item: TopIpDisplay, index: number) {
  return expandedTopIps.value.has(topIpKey(item, index))
    ? item.nodes
    : item.nodes.slice(0, TOP_IP_NODE_PREVIEW_LIMIT);
}

function toggleTopIpNodes(item: TopIpDisplay, index: number) {
  const key = topIpKey(item, index);
  const expanded = new Set(expandedTopIps.value);
  if (expanded.has(key)) expanded.delete(key);
  else expanded.add(key);
  expandedTopIps.value = expanded;
}

function nodeCodeLabel(node: TopIpNodeStat) {
  return node.nodeCode?.trim() || '节点编号未记录';
}

function nodeNameLabel(node: TopIpNodeStat) {
  return node.nodeName?.trim() || '节点名称未记录';
}

function nodeIpLabel(node: TopIpNodeStat) {
  const value = node.nodeIp?.trim();
  return value && value !== '0.0.0.0' ? value : '入口 IP 未记录';
}

function nodeCoverageLabel(value: number | null | undefined) {
  return typeof value === 'number' && Number.isFinite(value) && value < 100
    ? `节点明细覆盖 ${formatCompact(Math.max(0, value))}%`
    : '';
}

function selectedWindowSeconds() {
  if (timeRange.value === 'today') {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return Math.max(300, (now.getTime() - start.getTime()) / 1000);
  }
  return timeRangeDuration(timeRange.value) / 1_000;
}

function pointBandwidth(point: TrafficPoint) {
  if (typeof point.bandwidthBps === 'number' && Number.isFinite(point.bandwidthBps)) {
    return Math.max(0, point.bandwidthBps);
  }
  if (typeof point.bodyBytesSent === 'number' && Number.isFinite(point.bodyBytesSent)) {
    const seconds = typeof point.bucketSeconds === 'number' && Number.isFinite(point.bucketSeconds) && point.bucketSeconds > 0
      ? point.bucketSeconds
      : 3600;
    return Math.max(0, point.bodyBytesSent * 8 / seconds);
  }
  return null;
}

function recommendBandwidth(samples: BandwidthSample[]): Recommendation {
  const values = samples.map(sample => sample.value);
  const evidence = values.length ? values : [fallbackAverageBandwidth.value];
  const sorted = [...evidence].sort((left, right) => left - right);
  const p95 = sorted[Math.max(0, Math.ceil(sorted.length * 0.95) - 1)] ?? 0;
  const peak = sorted.at(-1) ?? 0;
  const sustainedRatio = peak > 0 ? values.filter(value => value >= peak * 0.8).length / Math.max(1, values.length) : 0;
  const segment = Math.max(1, Math.floor(values.length / 3));
  const early = average(values.slice(0, segment));
  const recent = average(values.slice(-segment));
  const growth = early > 0 ? (recent - early) / early : recent > 0 ? 1 : 0;
  const trendFactor = (sustainedRatio >= 0.25 ? 0.15 : 0) + (growth >= 0.25 ? 0.15 : 0);
  const bandwidth = niceCeiling(Math.max(1000, p95 * (1.3 + trendFactor)));

  let status: Recommendation['status'] = '充足';
  if (growth >= 0.5 || sustainedRatio >= 0.5) status = '建议升级';
  else if (growth >= 0.15 || sustainedRatio >= 0.25) status = '建议关注';

  return {
    bandwidth,
    status,
    lowSample: values.length < 12,
    reason: '按 P95、峰值持续时间与增长趋势评分，并预留至少 30% 安全余量。'
  };
}

function average(values: number[]) {
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0;
}

function niceCeiling(value: number) {
  if (!Number.isFinite(value) || value <= 1000) return 1000;
  const exponent = 10 ** Math.floor(Math.log10(value));
  const normalized = value / exponent;
  const step = [1, 2, 5, 10].find(candidate => normalized <= candidate) ?? 10;
  return step * exponent;
}

function numeric(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function numericOrNull(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function formatBytes(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '暂无数据';
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${formatCompact(value / 1024)} KB`;
  if (value < 1024 * 1024 * 1024) return `${formatCompact(value / 1024 / 1024)} MB`;
  return `${formatCompact(value / 1024 / 1024 / 1024)} GB`;
}

function formatBandwidth(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '暂无数据';
  const safe = Math.max(0, value);
  if (safe < 1_000_000) return `${formatCompact(safe / 1_000)} Kbps`;
  if (safe < 1_000_000_000) return `${formatCompact(safe / 1_000_000)} Mbps`;
  return `${formatCompact(safe / 1_000_000_000)} Gbps`;
}

function formatCompact(value: number) {
  if (value === 0) return '0';
  if (value >= 100) return Math.round(value).toLocaleString('zh-CN');
  if (value >= 10) return value.toFixed(1).replace(/\.0$/, '');
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function formatPercent(value: number | null) {
  return value === null ? '暂无数据' : `${formatCompact(value)}%`;
}

function formatNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('zh-CN') : '暂无数据';
}

function bucketLabel(value?: string) {
  if (!value) return '未知时间';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const date = `${String(parsed.getMonth() + 1).padStart(2, '0')}/${String(parsed.getDate()).padStart(2, '0')}`;
  const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
  if (granularity.value === '5m' || ['today', '1h', '6h', '24h'].includes(timeRange.value)) return time;
  return granularity.value === '1h' ? `${date} ${time}` : date;
}

function shareLabel(value: unknown) {
  return typeof value === 'number' ? `${formatCompact(value)}%` : '占比暂无';
}

function countryLabel(code?: string) {
  if (!code) return '国家待识别';
  const normalized = code.toUpperCase();
  try {
    const name = new Intl.DisplayNames(['zh-CN'], { type: 'region' }).of(normalized);
    return name && name !== normalized ? `${name}（${normalized}）` : normalized;
  } catch {
    return normalized;
  }
}

function topIpCountryLabel(item: TopIpStat) {
  if (item.countryName && item.countryCode) return `${item.countryName}（${item.countryCode}）`;
  if (item.countryName) return item.countryName;
  return countryLabel(item.countryCode);
}

function statusCodeMeaning(item: StatusCodeStat) {
  const exact: Record<number, string> = {
    200: '成功响应',
    301: '永久跳转',
    302: '临时跳转',
    304: '浏览器缓存命中',
    403: '被拦截或无权限',
    404: '页面或资源不存在',
    405: '请求方法不支持',
    500: '源站或服务异常',
    502: '网关或源站异常',
    503: '服务暂不可用',
    504: '源站响应超时'
  };
  if (exact[item.statusCode]) return exact[item.statusCode];
  if (item.statusCode >= 200 && item.statusCode < 300) return '成功响应';
  if (item.statusCode >= 300 && item.statusCode < 400) return '跳转或缓存响应';
  if (item.statusCode >= 400 && item.statusCode < 500) return '访问请求异常';
  if (item.statusCode >= 500) return '源站或服务异常';
  return '未分类状态';
}

function statusClassMeaning(item: StatusCodeStat) {
  const statusClass = item.statusClass || `${Math.floor(item.statusCode / 100)}xx`;
  const mapping: Record<string, string> = {
    '2xx': '请求成功',
    '3xx': '跳转或缓存复用',
    '4xx': '访问被拒或请求不正确',
    '5xx': '源站或平台服务异常'
  };
  return mapping[statusClass] || '状态类型暂无说明';
}

function errorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : '当前网络连接异常，暂时无法获取最新数据。';
}
</script>

<template>
  <TrafficQuotaCard v-if="showFreeQuota" />
  <section class="page traffic-page">
    <div class="page-title page-title-row">
      <div>
        <span class="eyebrow">TRAFFIC INTELLIGENCE</span>
        <h1>流量监控</h1>
        <p>查看真实带宽、流量趋势与访问分布，辅助判断业务高峰和容量余量。</p>
      </div>
      <el-button :loading="requesting" @click="loadTraffic(false)">刷新数据</el-button>
    </div>

    <el-alert v-if="error && summary" :title="`本次刷新失败，继续展示上次成功数据：${error}`" type="warning" show-icon />

    <el-card v-if="domains.length" class="filter-card realtime-filter-card">
      <div class="monitor-toolbar">
        <strong class="monitor-title">实时监控</strong>
        <span class="time-range-tabs" aria-label="时间范围">
          <button type="button" :class="{ active: timeRange === '1h' }" @click="selectTimeRange('1h')">最近1小时</button>
          <button type="button" :class="{ active: timeRange === '6h' }" @click="selectTimeRange('6h')">6小时</button>
          <button type="button" :class="{ active: timeRange === '24h' }" @click="selectTimeRange('24h')">24小时</button>
          <button type="button" :class="{ active: timeRange === '7d' }" @click="selectTimeRange('7d')">7天</button>
        </span>
        <label class="custom-range-filter" aria-label="精确时间范围">
          <el-date-picker
            v-model="customRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            :clearable="false"
            @change="applyCustomRange"
          />
        </label>
        <label class="domain-filter" aria-label="选择域名">
          <el-select v-model="selectedDomainId" @change="selectDomain">
            <el-option label="全部已添加域名" :value="ALL_DOMAINS" />
            <el-option v-for="domain in domains" :key="domain.id" :label="domain.domain" :value="domain.id" />
          </el-select>
        </label>
        <label class="refresh-interval" aria-label="自动刷新间隔">
          <span aria-hidden="true">↻</span>
          <select v-model.number="refreshIntervalSeconds" @change="restartAutoRefresh">
            <option v-for="seconds in REFRESH_INTERVAL_OPTIONS" :key="seconds" :value="seconds">{{ seconds }}秒</option>
          </select>
        </label>
        <button type="button" class="realtime-button" :class="{ active: realtime }" @click="returnToRealtime">
          ▶ 回到实时
        </button>
      </div>
      <div class="refresh-status">
        <span class="live-dot" aria-hidden="true"></span>
        页面可见时每 {{ refreshIntervalSeconds }} 秒自动刷新
        <span class="refresh-time">最近查询成功：{{ refreshedAt || '正在获取' }}</span>
      </div>
    </el-card>

    <TrafficCyclePanel v-if="hasConsoleSession()" />

    <el-skeleton v-if="loading && !summary" :rows="8" animated />

    <div v-else-if="!domains.length" class="state-panel no-domain-state">
      <div class="state-icon">+</div>
      <div>
        <h2>暂未添加任何域名</h2>
        <p>添加 CDN 接入域名或 301 跳转域名后，这里会开始展示真实流量。</p>
      </div>
      <a class="primary-link" href="/domains">去添加域名</a>
    </div>

    <div v-else-if="collectionState === 'error'" class="state-panel error-state">
      <div class="state-icon">!</div>
      <div>
        <h2>数据采集失败</h2>
        <p>{{ error }}</p>
        <small>最近查询成功：{{ refreshedAt || '暂无成功记录' }}</small>
      </div>
      <button type="button" class="primary-link" @click="loadTraffic(false)">重新获取</button>
    </div>

    <div v-else-if="collectionState === 'processing'" class="state-panel">
      <div class="state-icon processing">···</div>
      <div>
        <h2>统计处理中</h2>
        <p>接入日志已经提交，聚合完成后会自动显示。</p>
      </div>
      <button type="button" class="primary-link" @click="loadTraffic(false)">重新获取</button>
    </div>

    <template v-else-if="summary">
      <div v-if="collectionState === 'empty'" class="state-panel compact-state">
        <div class="state-icon">0</div>
        <div>
          <h2>当前时间范围暂未收到节点统计数据</h2>
          <p>可能暂时没有访问，也可能存在节点采集延迟；页面查询成功不代表节点已经完成统计上报。</p>
        </div>
      </div>

      <div class="metric-grid overview-grid">
        <el-card>
          <span>总请求数</span>
          <strong>{{ formatNumber(totalRequests) }}</strong>
          <small>所选范围累计请求</small>
        </el-card>
        <el-card>
          <span>总流量</span>
          <strong>{{ formatBytes(totalBytes) }}</strong>
          <small>响应体累计流量</small>
        </el-card>
        <el-card>
          <span>缓存命中率</span>
          <strong>{{ cacheHitRate === null ? '暂无数据' : formatPercent(cacheHitRate) }}</strong>
          <small>{{ cacheMetricDetail }}</small>
          <small>{{ cacheAllRequestDetail }}</small>
        </el-card>
        <el-card>
          <span>源站失败率</span>
          <strong>{{ originFailureRate === null ? '暂无数据' : formatPercent(originFailureRate) }}</strong>
          <small>{{ originMetricDetail }}</small>
        </el-card>
      </div>

      <el-card class="bandwidth-card">
        <template #header>
          <div class="card-heading">
            <div>
              <span class="eyebrow">CAPACITY SIGNAL</span>
              <h2>带宽趋势</h2>
            </div>
            <span class="recommendation-pill" :data-status="bandwidthRecommendation.status">
              {{ bandwidthRecommendation.status }}
            </span>
          </div>
        </template>

        <div class="collector-evidence" role="status">
          <p>{{ collectorDetail }}</p>
          <small>以上为当前采集状态。曲线空档表示未收到该时段样本，不能单凭空档判断网站中断或流量为零。</small>
          <small>可信平台探测已分离；未登记的手动测试仍可能计入统计，不能把测试命中率当作真实访客效果。</small>
        </div>
        <p v-if="bandwidthRecommendation.lowSample" class="sample-warning">样本较少，建议持续观察</p>
        <div v-if="nodeError" class="node-analysis-error" role="alert">
          <span>节点明细获取失败：{{ nodeError }}</span>
          <button type="button" :disabled="nodeRetrying" @click="retryNodeAnalytics">
            {{ nodeRetrying ? '正在重试' : '重试节点明细' }}
          </button>
        </div>
        <BandwidthTrendChart
          :total-points="trendPoints"
          :node-series="nodeAnalytics?.nodeSeries ?? []"
          :coverage="nodeAnalytics?.coverage ?? null"
          :time-range="timeRange"
          :granularity="granularity"
          :minimum-scale-max-bps="bandwidthRecommendation.bandwidth"
          :range-start="lastSuccessfulCoreQuery?.from ?? ''"
          :range-end="lastSuccessfulCoreQuery?.to ?? ''"
          :realtime="realtime"
          @pan="panTimeWindow"
          @return-realtime="returnToRealtime"
        />
      </el-card>

      <div class="security-monitor-grid">
        <el-card class="security-monitor-card">
          <template #header>DDoS 监控</template>
          <div class="security-monitor-body">
            <span class="risk-pill">{{ ddosRiskText }}</span>
            <h3>流量洪峰</h3>
            <p>结合峰值请求、峰值带宽和源站失败率判断突发风险。</p>
            <dl>
              <div><dt>峰值请求</dt><dd>{{ formatNumber(peakRequestPoint?.requestCount) }}</dd></div>
              <div><dt>峰值带宽</dt><dd>{{ formatBandwidth(peakBandwidth) }}</dd></div>
              <div><dt>当前风险</dt><dd>{{ ddosRiskText }}</dd></div>
            </dl>
          </div>
        </el-card>

        <el-card class="security-monitor-card">
          <template #header>CC 攻击监控</template>
          <div class="security-monitor-body">
            <span class="risk-pill">{{ ccRiskText }}</span>
            <h3>单 IP 请求占比</h3>
            <p>单个 IP 占比过高时，结合状态码判断集中刷接口或 CC 风险。</p>
            <dl>
              <div><dt>最高占比 IP</dt><dd>{{ topIpLeader?.clientIp || '暂无数据' }}</dd></div>
              <div><dt>单 IP 请求占比</dt><dd>{{ shareLabel(topIpLeader?.requestSharePercent) }}</dd></div>
              <div><dt>当前风险</dt><dd>{{ ccRiskText }}</dd></div>
            </dl>
          </div>
        </el-card>
      </div>

      <div class="analysis-grid">
        <el-card>
          <template #header>Top IP</template>
          <p class="top-ip-node-state">按请求数排列，最多列出前 100 个 IP；右侧按国家汇总全部请求，两列不会逐行对应。</p>
          <EmptyState v-if="!topIps.length" title="暂无 Top IP 数据" description="产生真实访问后会显示请求来源。" />
          <ul v-else class="top-ip-list">
            <li v-for="(item, index) in visibleTopIpDisplays" :key="topIpKey(item, index)" class="top-ip-item">
              <div class="top-ip-summary">
                <div class="top-ip-identity">
                  <strong class="top-ip-address">{{ item.stat.clientIp }}</strong>
                  <small>{{ topIpCountryLabel(item.stat) }}</small>
                </div>
                <span>请求 {{ formatNumber(item.stat.requestCount) }}</span>
                <span>流量 {{ formatBytes(item.stat.bodyBytesSent) }}</span>
                <span>{{ shareLabel(item.stat.requestSharePercent) }}</span>
              </div>
              <p v-if="nodeError" class="top-ip-node-state">节点明细暂不可用，请重试。</p>
              <p v-else-if="!item.nodes.length" class="top-ip-node-state">历史记录未包含访问节点信息</p>
              <div v-else class="top-ip-node-details">
                <p v-if="nodeCoverageLabel(item.breakdown?.nodeCoveragePercent)" class="top-ip-coverage">
                  {{ nodeCoverageLabel(item.breakdown?.nodeCoveragePercent) }}
                </p>
                <ul class="top-ip-node-list">
                  <li
                    v-for="node in visibleTopIpNodes(item, index)"
                    :key="node.nodeId"
                    class="top-ip-node-item"
                  >
                    <div>
                      <strong>{{ nodeCodeLabel(node) }} · {{ nodeNameLabel(node) }}</strong>
                      <small>{{ nodeIpLabel(node) }}</small>
                    </div>
                    <span>请求 {{ formatNumber(node.requestCount) }}</span>
                    <span>流量 {{ formatBytes(node.bodyBytesSent) }}</span>
                    <span>{{ shareLabel(node.requestSharePercent) }}</span>
                    <div class="top-ip-node-latency" title="访问节点的 TCP 往返延迟，按有效请求样本统计；非下载速度或请求处理耗时。无有效样本显示 —。">
                      <span>最快 {{ formatRtt(node.rttMinMs, node.rttSampleCount) }}</span>
                      <span>最慢 {{ formatRtt(node.rttMaxMs, node.rttSampleCount) }}</span>
                      <span>平均 {{ formatRtt(node.rttAvgMs, node.rttSampleCount) }}</span>
                    </div>
                  </li>
                </ul>
                <button
                  v-if="item.nodes.length > TOP_IP_NODE_PREVIEW_LIMIT"
                  type="button"
                  class="top-ip-node-toggle"
                  :aria-expanded="expandedTopIps.has(topIpKey(item, index))"
                  @click="toggleTopIpNodes(item, index)"
                >
                  {{ expandedTopIps.has(topIpKey(item, index)) ? '收起节点' : `查看全部 ${item.nodes.length} 个` }}
                </button>
              </div>
            </li>
          </ul>
          <button v-if="topIpDisplays.length > TOP_IP_PREVIEW" data-test="expand-top-ips" type="button" class="country-toggle" :aria-expanded="topIpsExpanded" @click="topIpsExpanded = !topIpsExpanded">
            {{ topIpsExpanded ? '收起更多 IP' : `展开更多 IP（已载入 ${topIpDisplays.length} 个）` }}
          </button>
          <details v-if="otherIpCountries.length" data-test="other-ip-countries" class="other-ip-countries">
            <summary>查看未在当前 IP 列表显示的国家 / 地区（{{ otherIpCountries.length }}）</summary>
            <p>以下为国家总量，包含未进入 IP 排名的访问；不代表新增或丢失的流量。</p>
            <ul class="stat-list"><li v-for="item in otherIpCountries" :key="item.countryCode">
              <strong>{{ countryLabel(item.countryCode) }}</strong><span>请求 {{ formatNumber(item.requestCount) }}</span><span>流量 {{ formatBytes(item.bodyBytesSent) }}</span>
            </li></ul>
          </details>
        </el-card>

        <el-card>
          <template #header>状态码分布</template>
          <EmptyState v-if="!statusCodes.length" title="暂无状态码数据" />
          <ul v-else class="stat-list">
            <li v-for="item in statusCodes" :key="item.statusCode">
              <div>
                <strong>HTTP {{ item.statusCode }}</strong>
                <small>{{ statusCodeMeaning(item) }}</small>
              </div>
              <span>{{ statusClassMeaning(item) }}</span>
              <span>请求 {{ formatNumber(item.requestCount) }}</span>
              <span>{{ shareLabel(item.requestSharePercent) }}</span>
            </li>
          </ul>
        </el-card>

        <el-card>
          <template #header>
            <div class="country-heading">
              <span>国家/地区</span>
              <small>GeoIP 2.0</small>
            </div>
          </template>
          <div v-if="!countries.length" class="empty-explain">
            <strong>国家数据统计处理中</strong>
            <p>请求与流量不受影响；GeoIP 2.0 聚合完成后会自动补全。</p>
          </div>
          <template v-else>
            <ul class="stat-list">
              <li data-test="country-summary-row" v-for="item in visibleCountries" :key="item.countryCode">
                <strong>{{ countryLabel(item.countryCode) }}</strong>
                <span>请求 {{ formatNumber(item.requestCount) }}</span>
                <span>流量 {{ formatBytes(item.bodyBytesSent) }}</span>
                <span>{{ shareLabel(item.requestSharePercent) }}</span>
              </li>
            </ul>
            <button
              v-if="hiddenCountryCount"
              type="button"
              class="country-toggle"
              @click="countriesExpanded = !countriesExpanded"
            >
              {{ countriesExpanded ? '收起' : '展开全部' }}
            </button>
          </template>
        </el-card>
      </div>

      <p class="last-success">最近查询成功：{{ refreshedAt || '暂无成功记录' }}</p>
    </template>
  </section>
</template>

<style scoped>
.other-ip-countries { margin-top: 16px; color: #285840; }
.other-ip-countries summary { cursor: pointer; line-height: 1.6; }
.other-ip-countries p { font-size: 12px; line-height: 1.6; }
.other-ip-countries summary:focus-visible { outline: 2px solid #17603e; outline-offset: 3px; }

.traffic-page {
  --traffic-ink: #142b21;
  --traffic-muted: #64756c;
  --traffic-green: #0f6948;
  --traffic-line: rgba(24, 70, 49, 0.12);
}

.eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #197353;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.filter-card {
  margin-bottom: 20px;
}

.realtime-filter-card :deep(.el-card__body) {
  padding: 14px 16px 11px;
}

.monitor-toolbar {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.monitor-title {
  flex: 0 0 auto;
  margin-right: 8px;
  color: var(--traffic-ink);
  font-size: 20px;
  letter-spacing: -0.03em;
}

.time-range-tabs {
  display: inline-flex;
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid #d7e2da;
  border-radius: 9px;
  background: #f8fbf8;
}

.time-range-tabs button {
  min-height: 36px;
  border: 0;
  border-right: 1px solid #e3ebe5;
  padding: 0 13px;
  color: #52675c;
  background: transparent;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.time-range-tabs button:last-child { border-right: 0; }
.time-range-tabs button.active { color: #0b5b3d; background: #edf8f0; }
.time-range-tabs button:focus-visible { position: relative; z-index: 1; outline: 2px solid #6ca98b; outline-offset: -2px; }

.custom-range-filter,
.domain-filter {
  min-width: 0;
}

.custom-range-filter {
  flex: 1 1 280px;
}

.custom-range-filter :deep(.el-date-editor) {
  width: 100%;
  min-height: 36px;
}

.realtime-button {
  min-height: 36px;
  flex: 0 0 auto;
  border: 1px solid #b9d5c5;
  border-radius: 10px;
  padding: 0 16px;
  color: #0d6746;
  background: #eef8f1;
  font-weight: 800;
  cursor: pointer;
}

.realtime-button.active {
  border-color: #0f6948;
  color: #fff;
  background: #0f6948;
}

.domain-filter {
  width: 180px;
  flex: 0 1 180px;
}

.refresh-interval {
  display: inline-flex;
  min-height: 36px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  border: 1px solid #d7e2da;
  border-radius: 9px;
  padding: 0 8px;
  color: #315846;
  background: #fff;
}

.refresh-interval select {
  border: 0;
  outline: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
}

.refresh-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  color: var(--traffic-muted);
  font-size: 13px;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2f9b62;
  box-shadow: 0 0 0 5px rgba(47, 155, 98, 0.1);
}

.refresh-time {
  margin-left: auto;
}

.state-panel {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 132px;
  margin-bottom: 20px;
  padding: 24px 28px;
  border: 1px solid var(--traffic-line);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 42px rgba(27, 66, 48, 0.08);
}

.state-panel h2,
.state-panel p {
  margin: 0;
}

.state-panel p {
  margin-top: 5px;
  color: var(--traffic-muted);
}

.state-panel small {
  display: block;
  margin-top: 8px;
  color: var(--traffic-muted);
}

.state-icon {
  display: grid;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  place-items: center;
  border-radius: 15px;
  color: #fff;
  background: var(--traffic-green);
  font-size: 23px;
  font-weight: 900;
}

.state-icon.processing {
  color: var(--traffic-green);
  background: #e8f3ec;
}

.error-state .state-icon {
  background: #bc493f;
}

.primary-link {
  margin-left: auto;
  border: 0;
  border-radius: 11px;
  padding: 11px 18px;
  color: #fff;
  background: var(--traffic-green);
  font: inherit;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

.compact-state {
  min-height: auto;
  padding-block: 18px;
}

.overview-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 20px;
}

.overview-grid :deep(.el-card__body) {
  display: grid;
  gap: 7px;
  min-height: 130px;
  align-content: center;
}

.overview-grid span,
.bandwidth-summary span {
  color: var(--traffic-muted);
  font-size: 13px;
  font-weight: 800;
}

.overview-grid strong,
.bandwidth-summary strong {
  color: var(--traffic-ink);
  font-size: clamp(24px, 2.2vw, 34px);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.04em;
}

.overview-grid small,
.bandwidth-summary small {
  color: #7a8a81;
  line-height: 1.45;
}

.bandwidth-card {
  margin-bottom: 20px;
}

.card-heading,
.country-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.card-heading h2 {
  margin: 0;
  color: var(--traffic-ink);
  font-size: 22px;
}

.recommendation-pill {
  border: 1px solid #b7d9c5;
  border-radius: 999px;
  padding: 7px 12px;
  color: #17613f;
  background: #edf7ef;
  font-size: 12px;
  font-weight: 900;
}

.recommendation-pill[data-status="建议关注"] {
  border-color: #e7cd8e;
  color: #835c0e;
  background: #fff8e6;
}

.recommendation-pill[data-status="建议升级"] {
  border-color: #efb5ae;
  color: #a23932;
  background: #fff0ee;
}

.bandwidth-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  margin-bottom: 12px;
  border: 1px solid var(--traffic-line);
  border-radius: 18px;
  background: var(--traffic-line);
}

.bandwidth-summary > div {
  display: grid;
  gap: 6px;
  min-height: 132px;
  align-content: center;
  padding: 18px;
  background: #fff;
}

.bandwidth-summary .recommended {
  background:
    radial-gradient(circle at 100% 0, rgba(232, 192, 87, 0.2), transparent 55%),
    #f7fbf5;
}

.bandwidth-summary .recommended strong {
  color: #0f6948;
}

.sample-warning {
  width: max-content;
  margin: 10px 0 14px;
  border-radius: 999px;
  padding: 6px 11px;
  color: #815d13;
  background: #fff5d9;
  font-size: 12px;
  font-weight: 800;
}

.node-analysis-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
  border: 1px solid #efb5ae;
  border-radius: 12px;
  padding: 11px 13px;
  color: #9c302c;
  background: #fff3f1;
  font-size: 13px;
  font-weight: 800;
}

.node-analysis-error button {
  min-height: 44px;
  flex: 0 0 auto;
  border: 1px solid #d88178;
  border-radius: 10px;
  padding: 8px 13px;
  color: #8a2824;
  background: #fff;
  font: inherit;
  font-weight: 900;
  cursor: pointer;
}

.node-analysis-error button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.security-monitor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 20px;
}

.security-monitor-body {
  position: relative;
  display: grid;
  gap: 7px;
  min-height: 184px;
  overflow: hidden;
  border: 1px solid var(--traffic-line);
  border-radius: 16px;
  padding: 16px;
  background:
    radial-gradient(circle at 100% 0, rgba(233, 192, 84, 0.17), transparent 9rem),
    #f8fbf6;
}

.security-monitor-body h3,
.security-monitor-body p {
  margin: 0;
}

.security-monitor-body h3 {
  color: #163f2a;
  font-size: 21px;
}

.security-monitor-body p {
  color: var(--traffic-muted);
  line-height: 1.55;
}

.security-monitor-body dl {
  display: grid;
  gap: 8px;
  margin: 8px 0 0;
}

.security-monitor-body dl div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  border-top: 1px solid rgba(18, 63, 42, 0.08);
  padding-top: 8px;
}

.security-monitor-body dt {
  color: var(--traffic-muted);
  font-size: 12px;
  font-weight: 800;
}

.security-monitor-body dd {
  margin: 0;
  color: var(--traffic-ink);
  font-weight: 900;
}

.risk-pill {
  width: max-content;
  border-radius: 999px;
  padding: 5px 11px;
  color: #12622d;
  background: #e7f5df;
  font-size: 12px;
  font-weight: 900;
}

.analysis-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.country-heading small {
  border-radius: 999px;
  padding: 4px 8px;
  color: #1c6746;
  background: #eaf5ed;
  font-size: 11px;
  font-weight: 900;
}

.top-ip-list,
.top-ip-node-list {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.top-ip-item {
  overflow: hidden;
  border: 1px solid rgba(24, 70, 49, 0.1);
  border-radius: 14px;
  background: rgba(243, 248, 243, 0.92);
}

.top-ip-summary,
.top-ip-node-item {
  display: grid;
  grid-template-columns: minmax(150px, 1.25fr) minmax(80px, 0.7fr) minmax(80px, 0.7fr) minmax(54px, 0.45fr);
  gap: 10px;
  align-items: center;
  padding: 12px;
  font-size: 13px;
}

.top-ip-identity,
.top-ip-node-item > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.top-ip-address,
.top-ip-node-item strong,
.top-ip-node-item small {
  overflow-wrap: anywhere;
}

.top-ip-identity small,
.top-ip-node-item small {
  color: var(--traffic-muted);
  font-size: 11px;
  font-weight: 800;
}

.top-ip-node-details,
.top-ip-node-state {
  margin: 0 12px 12px;
  border-top: 1px solid rgba(24, 70, 49, 0.1);
  padding-top: 10px;
}

.top-ip-node-state {
  color: var(--traffic-muted);
  font-size: 12px;
}

.top-ip-coverage {
  width: max-content;
  max-width: 100%;
  margin: 0 0 9px;
  border-radius: 999px;
  padding: 5px 9px;
  color: #835c0e;
  background: #fff4d5;
  font-size: 11px;
  font-weight: 900;
}

.top-ip-node-list {
  gap: 7px;
}

.top-ip-node-item {
  border-radius: 10px;
  padding: 9px 10px;
  background: rgba(255, 255, 255, 0.9);
}

.top-ip-node-toggle {
  width: 100%;
  min-height: 44px;
  margin-top: 9px;
  border: 1px solid #cfe0d5;
  border-radius: 10px;
  padding: 9px 12px;
  color: #17613f;
  background: #f7fbf8;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.stat-list {
  display: grid;
  gap: 9px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.stat-list li {
  display: grid;
  grid-template-columns: minmax(126px, 1.2fr) minmax(82px, 0.8fr) minmax(82px, 0.8fr) minmax(54px, 0.5fr);
  gap: 10px;
  align-items: center;
  padding: 11px 12px;
  border-radius: 12px;
  background: rgba(243, 248, 243, 0.92);
  font-size: 13px;
}

.stat-list li > div {
  display: grid;
  gap: 3px;
}

.stat-list small {
  color: var(--traffic-muted);
  font-size: 11px;
  font-weight: 800;
}

.empty-explain {
  display: grid;
  gap: 7px;
  border: 1px solid var(--traffic-line);
  border-radius: 14px;
  padding: 16px;
  color: var(--traffic-muted);
  background: #f7fbf4;
}

.empty-explain p {
  margin: 0;
  line-height: 1.6;
}

.country-toggle {
  width: 100%;
  margin-top: 11px;
  border: 1px solid #cfe0d5;
  border-radius: 10px;
  padding: 9px 12px;
  color: #17613f;
  background: #f7fbf8;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.last-success {
  margin: 14px 2px 0;
  color: var(--traffic-muted);
  font-size: 12px;
  text-align: right;
}

@media (max-width: 1180px) {
  .bandwidth-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .monitor-toolbar { flex-wrap: wrap; }
  .monitor-title { width: 100%; }
  .custom-range-filter { flex: 1 1 260px; }

  .analysis-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 820px) {
  .overview-grid,
  .bandwidth-summary,
  .security-monitor-grid {
    grid-template-columns: 1fr;
  }

  .monitor-toolbar { align-items: stretch; }
  .time-range-tabs { width: 100%; }
  .time-range-tabs button { flex: 1 1 25%; padding-inline: 7px; }
  .custom-range-filter,
  .domain-filter { width: 100%; flex: 1 1 100%; }
  .refresh-interval,
  .realtime-button { flex: 1 1 calc(50% - 4px); justify-content: center; }

  .refresh-status,
  .state-panel {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .refresh-time,
  .primary-link {
    margin-left: 0;
  }

  .state-panel {
    padding: 20px;
  }

  .stat-list li {
    grid-template-columns: 1fr 1fr;
  }

  .top-ip-summary,
  .top-ip-node-item {
    grid-template-columns: 1fr 1fr;
  }

  .node-analysis-error {
    align-items: stretch;
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .bandwidth-card,
  .overview-grid,
  .security-monitor-grid,
  .analysis-grid {
    animation: traffic-reveal 420ms ease-out both;
  }

  .security-monitor-grid {
    animation-delay: 70ms;
  }

  .analysis-grid {
    animation-delay: 120ms;
  }
}

@keyframes traffic-reveal {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.collector-evidence { margin-bottom: 16px; padding: 10px 14px; border: 1px solid #dce7e0; border-radius: 10px; color: #456155; }
.collector-evidence p { margin: 0 0 6px; font-size: 13px; }
.collector-evidence small { display: block; font-size: 12px; line-height: 1.6; }
</style>

<style scoped>
.top-ip-node-item > .top-ip-node-latency {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.top-ip-node-latency span {
  padding: 3px 8px;
  border-radius: 6px;
  color: #17613f;
  background: #e7f7e9;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  white-space: nowrap;
}
</style>
